/**
 * Asset-related types used by the Techline case assets DTOs.
 *
 * These types were previously imported from `@packages/models`; they are now defined natively
 * in `@packages/clients` so the clients package has no dependency on the models package.
 */

/**
 * The media sub-type of an asset, combining image, video, and file type identifiers
 * into a single enum for front-end use.
 */
export enum AssetMediaType {
    File = 'FILE',
    Original = 'ORIGINAL',
    OriginalOverlaid = 'ORIGINAL_OVERLAID',
    OriginalReoriented = 'ORIGINAL_REORIENTED',
    Thumbnail = 'THUMBNAIL',
    ThumbnailSmall = 'THUMBNAIL_SMALL',
    VideoStill = 'VIDEO_STILL',
}

/**
 * The processing state of an asset during and after upload.
 */
export enum AssetProcessingState {
    Complete = 'COMPLETE',
    Error = 'ERROR',
    Processing = 'PROCESSING',
    Uploading = 'UPLOADING',
}

/**
 * The high-level type of an asset (video, image, or generic file).
 */
export enum AssetType {
    File = 'FILE',
    Image = 'IMAGE',
    Video = 'VIDEO',
}

/**
 * A generic file media entry within an asset (non-image, non-video).
 */
export type AssetFileMedia = BaseAssetMedia & {
    /** The unique identifier of this file media entry. */
    assetFileId: string;
};

/**
 * An image media entry within an asset, containing image-specific identifiers
 * and dimensional data.
 */
export type AssetImageMedia = BaseDimensionalAssetMedia & {
    /** The unique identifier of this image media entry. */
    assetImageId: string;

    /** The image sub-type (e.g. ORIGINAL, THUMBNAIL). */
    assetImageTypeId: AssetMediaType;

    /** Human-readable description of the image type. */
    assetImageTypeIdDescription: string;
};

/**
 * A union of all possible media types that can appear within an asset.
 */
export type AssetMedia = AssetFileMedia | AssetImageMedia | AssetVideoMedia;

/**
 * A video media entry within an asset, containing video-specific identifiers,
 * dimensional data, and duration.
 */
export type AssetVideoMedia = BaseDimensionalAssetMedia & {
    /** The unique identifier of this video media entry. */
    assetVideoId: string;

    /** The video sub-type (e.g. ORIGINAL). */
    assetVideoTypeId: AssetMediaType;

    /** Human-readable description of the video type. */
    assetVideoTypeIdDescription: string;

    /** The duration of the video in seconds. */
    durationInSeconds: number;

    /** Human-readable description of the duration. */
    durationInSecondsDescription: string;
};

/**
 * Common fields shared by all asset media entries (images, videos, and files).
 */
export type BaseAssetMedia = {
    /** The MIME content type of the media (e.g. "image/jpeg"). */
    contentType: string;

    /** The timestamp when the media was created (ISO 8601). */
    createdTimestamp: string;

    /** Human-readable description of the creation timestamp. */
    createdTimestampDescription: string;

    /** The file size in bytes. */
    sizeInBytes: number;

    /** Human-readable description of the file size. */
    sizeInBytesDescription: string;

    /** The URL where the media content can be accessed. */
    url: string;
};

/**
 * Common fields shared by dimensional media entries (images and videos),
 * extending the base media fields with width and height.
 */
export type BaseDimensionalAssetMedia = BaseAssetMedia & {
    /** The height of the media in pixels. */
    height: number;

    /** Human-readable description of the height. */
    heightDescription: string;

    /** The width of the media in pixels. */
    width: number;

    /** Human-readable description of the width. */
    widthDescription: string;
};

/**
 * A presigned upload descriptor returned by the NorthStar API, containing the URL
 * and headers needed to upload an asset file to cloud storage.
 */
export type PresignedUpload = {
    /** The timestamp when the presigned URL expires (ISO 8601). */
    expirationTimestamp: string;

    /** Human-readable description of the expiration timestamp. */
    expirationTimestampDescription: string;

    /** The HTTP headers that must be included with the upload request. */
    httpHeaders: Record<string, string>;

    /** The HTTP method to use for the upload (PUT or POST). */
    httpMethod: 'POST' | 'PUT';

    /** The presigned URL to upload the file content to. */
    url: string;
};

/**
 * A Techline asset record as returned by the NorthStar API.
 *
 * Named `TechlineAsset` to distinguish from the worksheet `Asset` type in
 * `@packages/models/api/worksheets`.
 */
export type TechlineAsset = {
    /** The unique identifier of this asset. */
    assetId: string;

    /** The disposition identifier (e.g. "QMR", "ACCOUNT_AVATAR", "DOCUMENTATION"). */
    assetDispositionId: string;

    /** Human-readable description of the asset disposition. */
    assetDispositionIdDescription: string;

    /** The current processing state of the asset. */
    assetProcessingStateId: AssetProcessingState;

    /** Human-readable description of the processing state. */
    assetProcessingStateIdDescription: string;

    /** The high-level type of the asset (Video, Image, or File). */
    assetTypeId: AssetType;

    /** Human-readable description of the asset type. */
    assetTypeIdDescription: string;

    /** The timestamp when the asset was created (ISO 8601). */
    createdTimestamp: string;

    /** Human-readable description of the creation timestamp. */
    createdTimestampDescription: string;

    /** An error message, if the asset encountered a processing error. */
    error?: string;

    /** Whether the asset has a drawing/annotation overlay. */
    hasOverlay: boolean;

    /** Whether the asset was imported from the legacy Siebel system. */
    importedFromSiebel: boolean;

    /** The list of media entries (images, videos, files) belonging to this asset. */
    media: AssetMedia[];

    /** The display name of the asset. */
    name: string;

    /** A note attached to the asset. */
    note: string;

    /** The upload progress percentage (0-100). */
    percentUploaded: number;

    /** Human-readable description of the upload percentage. */
    percentUploadedDescription: string;

    /** The worksheet question submission source ID, if this asset was uploaded as a worksheet answer attachment. */
    worksheetQuestionSubmissionSourceId?: string;
};
